import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  DeleteOutlineOutlinedIcon,
  MoreVertIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { formatCurrency } from "utils/common";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import MuiDivider from "components/Divider";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import TransactionCollapse from "pages/Deal/FinancingEvents/TransactionCollapse";
import TransactionUpdateDialogButton from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionUpdateDialogButton";
import {
  extractInitialValuesFromTransaction,
  getTransactionTypeAndStatusFromTransaction,
} from "pages/Deal/FinancingEvents/utils";

import {
  ITransaction,
  transactionDelete,
  useFinancingEvents,
  useTransactions,
} from "entities/Deal/sdk";

export interface ITransactionGeneralInfo {
  transaction: ITransaction;
  financingEventId: number;
  isLast: boolean;
}

const TransactionGeneralInfo = ({
  transaction,
  financingEventId,
  isLast,
}: ITransactionGeneralInfo) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const params = useParams<{
    dealId: string;
  }>();

  const dealId = Number(params.dealId);
  const { mutate: mutateTransactions } = useTransactions({
    dealId,
    financingEventId,
  });

  const { mutate: mutateFinancingEvents } = useFinancingEvents({
    dealId,
  });

  const { transaction_type, status } = useMemo(
    () =>
      getTransactionTypeAndStatusFromTransaction({
        transaction,
      }),
    [transaction]
  );

  const onTransactionDelete = useCallback(() => {
    showConfirmationDialog({
      onConfirm: () =>
        transactionDelete({
          dealId,
          financingEventId,
          transactionId: transaction.id,
        })
          .then(() => {
            toast.successMessage("Transaction deleted successfully!");
            mutateTransactions();
            mutateFinancingEvents();
          })
          .catch(handleInvalidRequest),
      confirmButtonText: "Delete",
      message: "Are you sure you want to delete this transaction?",
    });
  }, [
    dealId,
    transaction,
    financingEventId,
    mutateTransactions,
    showConfirmationDialog,
    mutateFinancingEvents,
  ]);

  return (
    <>
      <Stack onClick={() => setIsCollapsed((open) => !open)}>
        <Button
          sx={{
            padding: theme.spacing(0.5, 0.75),
            width: "35px",
            justifyContent: "space-between",
            whiteSpace: "nowrap",
            minWidth: "max-content",
            "& .MuiButton-endIcon": {
              margin: 0,
            },
          }}
          endIcon={isCollapsed ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          color="secondary"
          variant="text"
        />
      </Stack>
      <Stack
        maxWidth="fit-content"
        borderRadius={1}
        sx={{ backgroundColor: colors.blue20, maxWidth: "fit-content" }}
        direction="row"
        padding={1}
        gap={1}
        alignItems="center"
      >
        <Tooltip title={transaction.transaction_identifier}>
          <Text
            padding={1}
            borderRadius={1}
            variant="text3"
            sx={{ backgroundColor: colors.black }}
            color={colors.white}
            maxWidth="fit-content"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {transaction.transaction_identifier}
          </Text>
        </Tooltip>
      </Stack>
      <Tooltip title={transaction.capital_provider}>
        <Text
          padding={1}
          sx={{ backgroundColor: colors.blue20 }}
          borderRadius={1}
          maxWidth="fit-content"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {transaction.capital_provider}
        </Text>
      </Tooltip>
      <Tooltip
        title={
          !_.isNil(transaction.financing_program)
            ? transaction.financing_program.name
            : "N/A"
        }
      >
        <Text
          padding={1}
          sx={{ backgroundColor: colors.blue20 }}
          maxWidth="fit-content"
          borderRadius={1}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {!_.isNil(transaction.financing_program)
            ? transaction.financing_program.name
            : "N/A"}
        </Text>
      </Tooltip>
      <Tooltip title={transaction_type}>
        <Text
          padding={1}
          maxWidth="fit-content"
          border={1}
          borderColor={colors.gray20}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {transaction_type}
        </Text>
      </Tooltip>
      <Tooltip
        title={`${formatCurrency({ currency: transaction.amount.currency })}
          ${transaction.amount.amount}`}
      >
        <Text
          padding={1}
          fontWeight={800}
          maxWidth="fit-content"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {formatCurrency({ currency: transaction.amount.currency })}
          {transaction.amount.amount}
        </Text>
      </Tooltip>
      <Tooltip title={transaction.description}>
        <Text
          padding={1}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {!_.isNil(transaction.description) ? transaction.description : "N/A"}
        </Text>
      </Tooltip>

      <Tooltip title={!_.isNil(status) ? status : "N/A"}>
        <Text
          maxWidth="fit-content"
          padding={1}
          sx={{ backgroundColor: colors.teal80 }}
          borderRadius={1}
          color={colors.white}
        >
          {!_.isNil(status) ? status : "N/A"}
        </Text>
      </Tooltip>
      <ButtonDropdown
        icon={<MoreVertIcon />}
        label=""
        variant="text"
        color="secondary"
        keepMenuMounted={true}
        sx={{
          padding: theme.spacing(0.5, 0.75),
          "& .MuiButton-endIcon": {
            margin: 0,
          },
          maxWidth: "fit-content",
          alignSelf: "center",
          justifySelf: "flex-end",
        }}
      >
        <TransactionUpdateDialogButton
          financingEventId={financingEventId}
          transactionId={transaction.id}
          initialValues={extractInitialValuesFromTransaction({ transaction })}
          transactionIdentifier={transaction.transaction_identifier}
          lastUpdatedInformation={
            _.isNil(transaction.updated_by)
              ? null
              : `Last edited on ${format(
                  parseJSON(transaction.updated_at),
                  FORMATS.FRONTEND.DATE
                )} by ${transaction.updated_by.first_name} ${transaction.updated_by.last_name}`
          }
        />
        <MenuItem onClick={onTransactionDelete}>
          <Stack gap={1} flexDirection="row" alignItems="center">
            <DeleteOutlineOutlinedIcon />
            <Text variant="text2">Delete</Text>
          </Stack>
        </MenuItem>
      </ButtonDropdown>
      <MuiDivider sx={{ gridColumn: "1 / -1" }} />
      <TransactionCollapse
        transaction={transaction}
        open={isCollapsed}
        isLast={isLast}
      />
    </>
  );
};

export default TransactionGeneralInfo;
